/**
 * When a message deserves a system notification.
 *
 * Kept as a pure function, separate from the socket wiring and the Notification
 * API, because "did this need to interrupt the user?" is the part with real
 * rules in it - and the only part worth testing without a browser.
 */

export interface NotifyContext {
  /** The arriving message. Only the routing fields matter here. */
  message: { senderId: string; channelId: string };
  currentUserId: string | null;
  /** The conversation on screen, or `null` when none is open. */
  activeChannelId: string | null;
  /** `false` when the tab is hidden - another tab, or the window minimised. */
  documentVisible: boolean;
  /** `false` when the window is in the background behind another application. */
  windowFocused: boolean;
  /** The user's preference. */
  enabled: boolean;
  permission: NotificationPermission;
}

/**
 * The rules, in order:
 *
 *   - Never notify for a message this user sent. The server echoes every send
 *     back to its own sender, so without this every message you post pops a
 *     notification at you.
 *   - Never notify without an explicit opt-in and a granted permission.
 *   - Never notify for the conversation the user is already looking at, in a
 *     focused, visible tab. They have read it by the time the toast would land.
 *
 * Anything else - a different channel, a hidden tab, an unfocused window - is a
 * message the user would otherwise miss, which is the whole point.
 */
export function shouldNotify(context: NotifyContext): boolean {
  const {
    message,
    currentUserId,
    activeChannelId,
    documentVisible,
    windowFocused,
    enabled,
    permission,
  } = context;

  if (!enabled || permission !== 'granted') {
    return false;
  }

  if (currentUserId !== null && message.senderId === currentUserId) {
    return false;
  }

  const watchingThisChannel =
    documentVisible && windowFocused && message.channelId === activeChannelId;

  return !watchingThisChannel;
}

/** Longest body we put in a notification before trailing off. */
const MAX_BODY = 140;

/** One line of message text, collapsed and clipped to fit a system toast. */
export function notificationBody(content: string): string {
  const line = content.replace(/\s+/g, ' ').trim();

  return line.length > MAX_BODY ? `${line.slice(0, MAX_BODY - 1)}…` : line;
}
